import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

// FIREBASE IMPORTS
import { getAuth, onAuthStateChanged } from "firebase/auth"; 
import { doc, getDoc, updateDoc, deleteDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

import { 
  Card, CardContent, Typography, Button, Grid, Box, Chip, GlobalStyles, Rating, CircularProgress,
  Checkbox, IconButton, Stack, Snackbar
} from "@mui/material";
import {
  FacebookShareButton, TwitterShareButton, EmailShareButton, WhatsappShareButton,
  FacebookIcon, TwitterIcon, EmailIcon, WhatsappIcon
} from "react-share";
import PrintIcon from '@mui/icons-material/Print';
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

import CookingModeDialog from "../components/CookingModeDialog";
import RecipeComments from "../components/RecipeComments";
import { scaleIngredientAmount, detectTimers } from "../utils/recipeStandardization";
import { addIngredientsToShoppingList } from "../utils/shoppingListService";

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(0); 
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Phase 1: Cooking Mode, Serving Scaler, & Interactive Checklists
  const [currentServings, setCurrentServings] = useState(4);
  const [cookingModeOpen, setCookingModeOpen] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState({});
  const [checkedSteps, setCheckedSteps] = useState({});

  const handleToggleIngredient = (index) => {
    setCheckedIngredients(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleToggleStep = (index) => {
    setCheckedSteps(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleResetChecklist = () => {
    setCheckedIngredients({});
    setCheckedSteps({});
  };

  const [snackbarMessage, setSnackbarMessage] = useState(null);

  const handleAddToShoppingList = () => {
    if (!recipe?.ingredients || recipe.ingredients.length === 0) {
      alert("No ingredients in this recipe to add.");
      return;
    }
    const scaledIngredients = recipe.ingredients.map(ing => ({
      ...ing,
      amount: scaleIngredientAmount(ing.amount, scaleMultiplier)
    }));

    addIngredientsToShoppingList(scaledIngredients, recipe.recipe_name);
    setSnackbarMessage(`Added ${scaledIngredients.length} ingredients to your grocery list!`);
  };

  const shareUrl = window.location.href;

  // ----------------------------------------------------------------------
  // 📡 API: Fetch Recipe from Firestore
  // ----------------------------------------------------------------------
  const fetchRecipe = useCallback(async () => {
    setLoading(true);
    try {
      const docRef = doc(db, "recipes", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();

        let displayAuthor = data.username || "Chef";
        if (displayAuthor.includes("@")) {
          displayAuthor = displayAuthor.split("@")[0];
        }
        if (data.user_id) {
          try {
            const uDoc = await getDoc(doc(db, "users", data.user_id));
            if (uDoc.exists()) {
              const uData = uDoc.data();
              if (uData.username && !uData.username.includes("@")) {
                displayAuthor = uData.username;
              } else if (uData.first_name || uData.firstName) {
                displayAuthor = `${uData.first_name || uData.firstName} ${uData.last_name || uData.lastName || ""}`.trim();
              }
            }
          } catch (e) {}
        }

        setRecipe({
          id: docSnap.id,
          recipe_name: data.recipe_name || "Untitled Recipe",
          username: displayAuthor,
          description: data.description || "No description provided.",
          cuisine: data.cuisine || "General",
          prep_time: data.prep_time || 0,
          cook_time: data.cook_time || 0,
          servings: data.servings || 1,
          difficulty: data.difficulty || "Medium",
          ingredients: data.ingredients || [],
          steps: data.steps || [],
          tags: data.tags || [],
          images: data.images || [],
          rating: data.rating || 0,
          rating_count: data.rating_count || 0,
          user_id: data.user_id,
          original_recipe: data.original_recipe || null
        });

        const initialServings = Math.max(1, parseInt(data.servings, 10) || 1);
        setCurrentServings(initialServings);
        
        const currentUid = auth.currentUser ? auth.currentUser.uid : null;
        if (currentUid && data.raters && data.raters[currentUid]) {
          setUserRating(data.raters[currentUid]);
        } else {
          setUserRating(0);
        }
      } else {
        setError("Recipe not found. It may have been deleted.");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setError("Failed to load recipe.");
    } finally {
      setLoading(false);
    }
  }, [id, auth]);

  useEffect(() => {
    // Check if user is logged in to manage Edit/Delete button visibility
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUserId(user.uid);
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists() && userDoc.data().role === "admin") {
            setIsAdmin(true);
          }
        } catch (e) {
          console.error("Error checking admin status:", e);
        }
      } else {
        setCurrentUserId(null);
        setIsAdmin(false);
      }
      fetchRecipe();
    });

    return () => unsubscribe();
  }, [auth, fetchRecipe]);

  // ----------------------------------------------------------------------
  // 🗑 ACTION: Delete
  // ----------------------------------------------------------------------
  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this recipe?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "recipes", id));
      navigate("/recipes");
    } catch (err) {
      console.error(err);
      alert("Failed to delete. You might not have permission.");
    }
  };

  // ----------------------------------------------------------------------
  // ✨ ACTION: Spice (Remix/Duplicate)
  // ----------------------------------------------------------------------
  const handleSpice = async () => {
    const user = auth.currentUser;
    if (!user) return navigate('/login');
    
    const confirmSpice = window.confirm("Ready to Spice It Up? This creates a copy in your profile for you to edit.");
    if (!confirmSpice) return;

    try {
        // Fetch current user's name to attach to the new recipe
        const userDoc = await getDoc(doc(db, "users", user.uid));
        let authorName = "Chef";
        if (userDoc.exists()) {
          const uData = userDoc.data();
          if (uData.username && !uData.username.includes("@")) {
            authorName = uData.username;
          } else if (uData.first_name || uData.firstName) {
            authorName = `${uData.first_name || uData.firstName} ${uData.last_name || uData.lastName || ""}`.trim();
          } else if (uData.username) {
            authorName = uData.username.split("@")[0];
          } else if (user.displayName && !user.displayName.includes("@")) {
            authorName = user.displayName;
          } else {
            authorName = user.email ? user.email.split("@")[0] : "Chef";
          }
        } else {
          authorName = (user.displayName && !user.displayName.includes("@"))
            ? user.displayName
            : (user.email ? user.email.split("@")[0] : "Chef");
        }

        // Create the duplicated data
        const spiceData = {
            ...recipe,
            recipe_name: `${recipe.recipe_name} (Spiced Up)`,
            user_id: user.uid,
            username: authorName,
            author_email: user.email,
            created_at: serverTimestamp(),
            likes: 0,
            is_featured: false,
            original_recipe: {
                id: recipe.id,
                name: recipe.recipe_name,
                username: recipe.username
            }
        };
        
        // Remove the old ID so Firestore generates a new one
        delete spiceData.id;

        // Save to Firestore
        const newRecipeRef = await addDoc(collection(db, "recipes"), spiceData);
        
        // Send user to the edit page of their new copy
        navigate(`/edit-recipe/${newRecipeRef.id}`);
    } catch (err) {
        console.error(err);
        alert("Failed to duplicate recipe.");
    }
  };

  // ----------------------------------------------------------------------
  // ⭐ ACTION: Rate (Fully Functional Firestore Logic)
  // ----------------------------------------------------------------------
  const handleRatingChange = async (event, newValue) => {
    if (!newValue) return; // Prevent clearing the rating completely
    
    const user = auth.currentUser;
    if (!user) {
        alert("Please log in to rate recipes.");
        return navigate('/login');
    }

    try {
        // Optimistic UI update for a fast feel
        setUserRating(newValue);

        const recipeRef = doc(db, "recipes", id);
        const recipeSnap = await getDoc(recipeRef);
        
        if (recipeSnap.exists()) {
            const data = recipeSnap.data();
            
            // Get the existing raters object, or create an empty one
            const raters = data.raters || {};
            
            // Save or update the current user's specific vote
            raters[user.uid] = newValue; 

            // Recalculate the new average and total count
            const scores = Object.values(raters);
            const newCount = scores.length;
            const newTotal = scores.reduce((acc, curr) => acc + curr, 0);
            const newAvg = Number((newTotal / newCount).toFixed(1)); // Round to 1 decimal

            // Update the database
            await updateDoc(recipeRef, {
                raters: raters,
                rating: newAvg,
                rating_count: newCount
            });

            // Update the local page state to show the new average immediately
            setRecipe(prev => ({ ...prev, rating: newAvg, rating_count: newCount }));
        }
    } catch (err) {
        console.error("Failed to submit rating", err);
        alert("Failed to submit rating. Please try again.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
    </Box>
  );

  if (error || !recipe) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <Typography color="error">{error || "Recipe not found."}</Typography>
    </Box>
  );

  const isOwner = currentUserId === recipe.user_id;
  const canEditOrDelete = isOwner || isAdmin;
  const imageToShow = (recipe.images && recipe.images.length > 0 && recipe.images[0]) 
    ? recipe.images[0] 
    : (recipe.image || "/no-image.png");
  const recipeTitle = `Check out this recipe for ${recipe.recipe_name}!`;

  const baseServings = Math.max(1, parseInt(recipe?.servings, 10) || 1);
  const scaleMultiplier = currentServings / baseServings;

  return (
    <>
      <GlobalStyles styles={{
        '@media print': {
          'header, .MuiAppBar-root': { display: 'none !important' },
          'body': { backgroundColor: 'white' },
          '.MuiPaper-root': { boxShadow: 'none !important', border: 'none !important', margin: '0 !important' },
          '.MuiContainer-root': { maxWidth: '100% !important', padding: '0 !important' },
          'button': { display: 'none !important' }
        }
      }} />

      <Box
        sx={{
          height: { xs: 220, sm: 300 },
          backgroundImage: `url(${imageToShow})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
          boxShadow: 3,
          '@media print': {
             height: 'auto', backgroundImage: 'none', boxShadow: 'none', border: 'none', marginBottom: 2
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            borderBottomLeftRadius: 10, borderBottomRightRadius: 10,
            '@media print': { position: 'static', backgroundColor: 'transparent', color: 'black', p: 0, display: 'block' }
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              color: 'white', 
              fontFamily: "'Patrick Hand', cursive", 
              fontSize: { xs: '1.85rem', sm: '2.5rem', md: '3rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)', 
              textAlign: 'center', 
              px: 2,
              lineHeight: 1.2,
              '@media print': { color: 'black', textShadow: 'none', textAlign: 'left', px: 0 }
            }}
          >
            {recipe.recipe_name}
          </Typography>
          
          {recipe.original_recipe && (
            <Typography 
                variant="subtitle1" 
                onClick={() => navigate(`/recipes/${recipe.original_recipe.id}`)}
                sx={{ 
                    color: '#ffb74d', 
                    cursor: 'pointer', textDecoration: 'underline', mt: 1, fontWeight: 'bold',
                    '&:hover': { color: '#fff' },
                    '@media print': { color: 'black', textDecoration: 'none' }
                }}
            >
                ✨ Spiced Up from {recipe.original_recipe.name} by {recipe.original_recipe.username}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, '@media print': { display: 'none' } }}>
             <Rating value={recipe.rating || 0} readOnly precision={0.5} icon={<StarIcon fontSize="inherit" sx={{ color: '#ffc107' }} />} emptyIcon={<StarIcon fontSize="inherit" sx={{ color: 'rgba(255, 255, 255, 0.3)' }} />} />
             <Typography variant="body1" sx={{ color: 'white', ml: 1, fontWeight: 'bold' }}>
                 {recipe.rating || 0} ({recipe.rating_count} reviews)
             </Typography>
          </Box>

        </Box>
      </Box>

      <Card sx={{ maxWidth: 800, margin: { xs: "12px auto", sm: "20px auto" }, padding: { xs: 1.5, sm: 2 }, borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, mb: 2 }}>
             {recipe.username ? (
                <Typography variant="subtitle1" color="textSecondary">Created by: {recipe.username.includes("@") ? recipe.username.split("@")[0] : recipe.username}</Typography>
              ) : <Box />}
             
             <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', width: { xs: '100%', sm: 'auto' }, '@media print': { display: 'none' } }}>
                <Button 
                    variant="contained" 
                    startIcon={<RestaurantMenuIcon />} 
                    onClick={() => setCookingModeOpen(true)} 
                    sx={{ 
                      backgroundColor: '#2e7d32', 
                      '&:hover': { backgroundColor: '#1b5e20' },
                      fontWeight: 'bold',
                      boxShadow: 2,
                      width: { xs: '100%', sm: 'auto' },
                      py: { xs: 1.1, sm: 0.8 },
                      fontSize: { xs: '1rem', sm: '0.875rem' }
                    }}
                >
                    Cooking Mode
                </Button>

                <Button 
                    variant="contained" 
                    startIcon={<AutoAwesomeIcon />} 
                    onClick={handleSpice} 
                    sx={{ 
                      backgroundColor: '#D28415', 
                      '&:hover': { backgroundColor: '#b36b10' },
                      flexGrow: { xs: 1, sm: 0 },
                      py: { xs: 0.9, sm: 0.8 }
                    }}
                >
                    Spice It Up
                </Button>

                <Button 
                    variant="outlined" 
                    startIcon={<AddShoppingCartIcon />} 
                    onClick={handleAddToShoppingList}
                    sx={{ 
                      borderColor: '#D28415', 
                      color: '#D28415', 
                      fontWeight: 'bold',
                      flexGrow: { xs: 1, sm: 0 },
                      py: { xs: 0.9, sm: 0.8 },
                      '&:hover': { borderColor: '#b36b10', bgcolor: 'rgba(210,132,21,0.08)' } 
                    }}
                >
                    Add to Grocery List
                </Button>

                <Button 
                    variant="outlined" 
                    startIcon={<PrintIcon />} 
                    onClick={handlePrint} 
                    sx={{ py: { xs: 0.9, sm: 0.8 }, display: { xs: 'none', sm: 'inline-flex' } }}
                >
                    Print
                </Button>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: { xs: 0.5, sm: 0 } }}>
                  <FacebookShareButton url={shareUrl} quote={recipeTitle}><FacebookIcon size={32} round /></FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={recipeTitle}><TwitterIcon size={32} round /></TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={recipeTitle} separator=":: "><WhatsappIcon size={32} round /></WhatsappShareButton>
                  <EmailShareButton url={shareUrl} subject={recipe.recipe_name} body={`${recipeTitle}\n\n${shareUrl}`}><EmailIcon size={32} round /></EmailShareButton>
                </Box>
             </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 2, '@media print': { display: 'none' } }}>
             <Typography component="legend" sx={{ mr: 2, fontWeight: 'bold' }}>
                {userRating > 0 ? "Your Rating:" : "Rate this recipe:"}
             </Typography>
             <Rating name="user-rating" value={userRating} onChange={handleRatingChange} size="large" />
            {userRating > 0 && <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>(Click to change)</Typography>}
          </Box>

          <Typography variant="body1" paragraph><strong>Description:</strong> {recipe.description}</Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
             <Grid item xs={6} sm={4}><Typography variant="body1"><strong>Cuisine:</strong> {recipe.cuisine}</Typography></Grid>
             <Grid item xs={6} sm={4}><Typography variant="body1"><strong>Prep Time:</strong> {recipe.prep_time} min</Typography></Grid>
             <Grid item xs={6} sm={4}><Typography variant="body1"><strong>Cook Time:</strong> {recipe.cook_time} min</Typography></Grid>
             <Grid item xs={12} sm={4}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                 <Typography variant="body1"><strong>Servings:</strong></Typography>
                 <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#f0ede6', borderRadius: 2, px: 0.5, '@media print': { display: 'none' } }}>
                   <IconButton 
                     size="small" 
                     onClick={() => setCurrentServings(prev => Math.max(1, prev - 1))}
                     disabled={currentServings <= 1}
                     sx={{ p: 0.75, minWidth: 36, minHeight: 36 }}
                   >
                     <RemoveIcon fontSize="small" />
                   </IconButton>
                   <Typography variant="body2" sx={{ fontWeight: 'bold', px: 1, minWidth: 28, textAlign: 'center' }}>
                     {currentServings}
                   </Typography>
                   <IconButton 
                     size="small" 
                     onClick={() => setCurrentServings(prev => prev + 1)}
                     sx={{ p: 0.75, minWidth: 36, minHeight: 36 }}
                   >
                     <AddIcon fontSize="small" />
                   </IconButton>
                 </Box>
                 <Typography variant="body1" sx={{ display: 'none', '@media print': { display: 'inline' } }}>
                   {currentServings}
                 </Typography>
                 {scaleMultiplier !== 1 && (
                   <Chip 
                     label={`${scaleMultiplier}x`} 
                     size="small" 
                     color="warning" 
                     variant="outlined" 
                     onDelete={() => setCurrentServings(baseServings)}
                     deleteIcon={<RestartAltIcon />}
                     title="Reset to original servings"
                     sx={{ height: 22, fontSize: '0.75rem', '@media print': { display: 'none' } }}
                   />
                 )}
               </Box>
             </Grid>
             <Grid item xs={6} sm={4}><Typography variant="body1"><strong>Difficulty:</strong> {recipe.difficulty}</Typography></Grid>
          </Grid>

          {/* INGREDIENTS WITH CHECKLIST & DYNAMIC SCALING */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
            <Typography variant="h5" sx={{ fontFamily: "'Patrick Hand', cursive" }}>
              Ingredients {scaleMultiplier !== 1 ? `(Scaled for ${currentServings} servings)` : `(${currentServings} servings)`}
            </Typography>
            {(Object.values(checkedIngredients).some(Boolean) || Object.values(checkedSteps).some(Boolean)) && (
              <Button size="small" onClick={handleResetChecklist} sx={{ '@media print': { display: 'none' } }}>
                Reset Checklist
              </Button>
            )}
          </Box>

          {recipe.ingredients && recipe.ingredients.length > 0 ? (
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {recipe.ingredients.map((ingredient, index) => {
                const scaledAmount = scaleIngredientAmount(ingredient.amount, scaleMultiplier);
                const isChecked = !!checkedIngredients[index];
                return (
                  <Box 
                    component="li" 
                    key={index} 
                    onClick={() => handleToggleIngredient(index)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      py: 0.6,
                      px: 1,
                      borderRadius: 1.5,
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: '#f5f2eb' }
                    }}
                  >
                    <Checkbox 
                      checked={isChecked} 
                      onChange={() => handleToggleIngredient(index)}
                      size="small"
                      sx={{ p: 0.5, mr: 1, color: '#D28415', '&.Mui-checked': { color: '#2e7d32' }, '@media print': { display: 'none' } }}
                    />
                    <Typography 
                      variant="body1"
                      sx={{ 
                        textDecoration: isChecked ? 'line-through' : 'none',
                        color: isChecked ? 'text.secondary' : 'text.primary',
                        fontSize: '1.05rem'
                      }}
                    >
                      <strong>{scaledAmount} {ingredient.measurement_name || ingredient.measurement_unit || ""}</strong> {ingredient.ingredient_name || ""}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : <Typography>No ingredients listed.</Typography>}

          {/* STEPS WITH CHECKLIST & TIMERS */}
          <Typography variant="h5" sx={{ marginTop: 3, mb: 1, fontFamily: "'Patrick Hand', cursive" }}>Steps</Typography>
          {recipe.steps && recipe.steps.length > 0 ? (
            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {recipe.steps.map((step, index) => {
                const isChecked = !!checkedSteps[index];
                const timers = detectTimers(step.instruction);
                return (
                  <Box 
                    component="li" 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      py: 1,
                      px: 1,
                      mb: 1,
                      borderRadius: 2,
                      borderLeft: `4px solid ${isChecked ? '#2e7d32' : '#D28415'}`,
                      bgcolor: isChecked ? 'rgba(46, 125, 50, 0.05)' : '#faf8f5',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Checkbox 
                      checked={isChecked} 
                      onChange={() => handleToggleStep(index)}
                      size="small"
                      sx={{ p: 0.5, mr: 1, mt: 0.2, color: '#D28415', '&.Mui-checked': { color: '#2e7d32' }, '@media print': { display: 'none' } }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          lineHeight: 1.6,
                          textDecoration: isChecked ? 'line-through' : 'none',
                          color: isChecked ? 'text.secondary' : 'text.primary'
                        }}
                      >
                        <strong style={{ marginRight: '6px' }}>{index + 1}.</strong>
                        {step.instruction}
                      </Typography>
                      {timers.length > 0 && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, '@media print': { display: 'none' } }}>
                          {timers.map((t, tIdx) => (
                            <Chip
                              key={tIdx}
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`⏱️ Start ${t.label}`}
                              onClick={() => setCookingModeOpen(true)}
                              title="Open Cooking Mode to run this timer"
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : <Typography>No steps provided.</Typography>}

          <Typography variant="h6" sx={{ marginTop: 2 }}>Tags</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {recipe.tags && recipe.tags.length > 0 ? (
                recipe.tags.map((tag, index) => <Chip key={index} label={tag} variant="outlined" sx={{ '@media print': { border: 'none', padding: 0, margin: 0 } }} />)
            ) : <Typography variant="body2" color="textSecondary">No tags available.</Typography>}
          </Box>

          <Grid container spacing={2} sx={{ marginTop: 4, justifyContent: 'center', '@media print': { display: 'none' } }}>
            {canEditOrDelete && (
              <>
                <Grid item><Button variant="contained" color="primary" onClick={() => navigate(`/edit-recipe/${id}`)}>Edit Recipe</Button></Grid>
                <Grid item><Button variant="contained" color="error" onClick={handleDelete}>Delete Recipe</Button></Grid>
              </>
            )}
            <Grid item><Button variant="outlined" onClick={() => navigate("/recipes")}>Back to Recipes</Button></Grid>
          </Grid>

        </CardContent>
      </Card>

      {/* Recipe Comments & Reviews */}
      <RecipeComments
        recipeId={id}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        userRating={userRating}
        onRatingChange={handleRatingChange}
      />

      {/* Cooking Mode Fullscreen Dialog */}
      <CookingModeDialog
        open={cookingModeOpen}
        onClose={() => setCookingModeOpen(false)}
        recipe={recipe}
        currentServings={currentServings}
        onServingsChange={setCurrentServings}
        checkedIngredients={checkedIngredients}
        onToggleIngredient={handleToggleIngredient}
        checkedSteps={checkedSteps}
        onToggleStep={handleToggleStep}
      />
      {/* Feedback Snackbar */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </>
  );
};

export default RecipeDetail;